<template>
    <div class="login-wrapper">
        <div class="content">
            <img class="logo" src="../../assets/warchest_logo_hq.png" />
            <div class="subtitle">
                The Unofficial War Chest Online
            </div>
            <form class="login" v-on:submit.prevent="onSubmit">
                <div class="form-control-wrapper">
                    <input
                        class="form-control"
                        v-model="form.email"
                        placeholder="Email"
                        name="email"
                        autocomplete="off"
                        @input="onInputChange"
                    />
                    <div class="form-error" v-if="errors.email">
                        {{ errors.email }}
                    </div>
                </div>
                <div class="form-control-wrapper form-control-wrapper--username" v-if="signUp">
                    <div class="form-control-with-count">
                        <input
                            class="form-control"
                            v-model="form.username"
                            placeholder="Username"
                            name="username"
                            autocomplete="off"
                            maxlength="12"
                            @input="onInputChange"
                        />
                        <span class="char-count">{{ usernameCharacterCount }}/{{ maxUsernameLength }}</span>
                    </div>
                    <div class="form-error" v-if="errors.username">
                        {{ errors.username }}
                    </div>
                </div>
                <div class="form-control-wrapper">
                    <input
                        class="form-control"
                        v-model="form.password"
                        placeholder="Password"
                        name="password"
                        type="password"
                        autocomplete="off"
                        @input="onInputChange"
                    />
                    <div class="form-error" v-if="errors.password">
                        {{ errors.password }}
                    </div>
                </div>
                <div class="form-control-wrapper" v-if="signUp && accessCodeRequired">
                    <input
                        class="form-control"
                        v-model="form.accessCode"
                        placeholder="Access Code"
                        name="accessCode"
                        autocomplete="off"
                        @input="onInputChange"
                    />
                    <div class="form-error" v-if="errors.accessCode">
                        {{ errors.accessCode }}
                    </div>
                </div>
                <div class="form-control-wrapper recaptcha-control-wrapper" v-show="signUp">
                    <div class="g-recaptcha" ref="recaptcha">
                    </div>
                    <div class="form-error" v-if="errors.recaptcha">
                        {{ errors.recaptcha }}
                    </div>
                </div>
                <div class="form-control-wrapper recaptcha-hint" v-if="signUp && showRecaptchaHint">
                    If you cannot see the ReCaptcha, try refreshing the page.
                </div>
                <div class="form-control-wrapper">
                    <input class="btn btn-primary btn-block" type="submit"
                        :value="signUp ? 'Sign up' : 'Login'" />
                </div>
                <span class="toggle-form-btn clickable" @click="onSwitchFormType">
                    {{ signUp ? 'Already registered? Click here to login.' : 'Sign up' }}
                </span>
            </form>
            <span class="toggle-form-btn clickable reset-password-btn">
                <router-link :to="{ name: 'password-reset', params: { email: form.email }}" class="link">
                    Forgot your password?
                </router-link>
            </span>
			<div class="partition" v-if="signUp"></div>
			<div class="login-terms-and-conditions" v-if="signUp">
				By signing up you agree to our
				<span class="link" @click="openTermsAndConditionsModal">
					Terms & Conditions</span>.
			</div>
        </div>
    </div>
</template>

<script lang="ts">
import api from '../../helpers/api';
import { ACCESS_CODE_REQUIRED } from '@/helpers/env';

const MAX_USERNAME_LENGTH = 12;

export default {
    name: 'Login',
    computed: {
        defaultAction() {
            return String(this.$route.params.action).toLowerCase();
        },
        defaultRedirect() {
            const { redirect } = this.$route.params;
            return redirect ? String(redirect).toLowerCase() : null;
        },
        usernameCharacterCount() {
            return this.form.username.length;
        },
        maxUsernameLength() {
            return MAX_USERNAME_LENGTH;
        },
    },
    mounted() {
        if (this.defaultAction === 'signup') {
            this.signUp = true;
        }

        if (this.defaultRedirect) {
            this.redirect = this.defaultRedirect;
        }

        try {
            window['grecaptcha'].render(this.$refs['recaptcha'], {
                sitekey: '6LcsIv4UAAAAANRU1PiEW4WhFg8uiRkNQ1KbQ-x_',
                callback: this.onRecaptchaSuccess,
            });
        } catch {
            this.showRecaptchaHint = true;
        }
    },
    watch: {
        '$route.params.action': function setDefaultAction() {
            if (this.defaultAction === 'signup') {
                this.signUp = true;
            } else {
                this.signUp = false;
            }
        },
        '$route.params.redirect': function setDefaultRedirect() {
            if (this.defaultRedirect) {
                this.signUp = this.defaultRedirect;
            } else {
                this.redirect = null;
            }
        },
    },
    data() {
        return {
            signUp: false,
            redirect: null,
            form: {
                email: '',
                username: '',
                password: '',
                discord: null,
                recaptcha: null,
                accessCode: '',
            },
            errors: {
                email: null,
                username: null,
                password: null,
                recaptcha: null,
                accessCode: null,
            },
            accessCodeRequired: ACCESS_CODE_REQUIRED === 'true',
            showRecaptchaHint: false,
        };
    },
    methods: {
        onSwitchFormType() {
            this.signUp = !this.signUp;
            this.errors = {
                email: null,
                username: null,
                password: null,
                recaptcha: null,
                accessCode: null,
            };
        },
        validateAccessCode(text, isSignUp) {
            return isSignUp && this.accessCodeRequired && String(text).trim().length;
        },
        validateEmail(email) {
            return /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/i.test(email);
        },
        validatePassword(text, isSignUp) {
            const password = String(text).trim();
            return Boolean((!isSignUp && password.length) || (isSignUp && password.length >= 8));
        },
        validateUsername(text, isSignUp) {
            const username = String(text).trim();
            return !isSignUp || (isSignUp && username.length >= 3);
        },
        validateForm(form, isSignUp) {
            const { email, username, password, recaptcha, accessCode } = form;
            const { errors } = this;

            const passwordMsg = isSignUp ? 'Please enter a password at least 8 characters long.' :
                'Please enter a password.';
            const usernameMsg = 'Please enter a username at least 3 characters long.';
            const emailMsg = 'Please enter a valid email.';
            const recaptchaMsg = 'Please click on the above reCaptcha checkbox';
            const accessCodeMsg = 'Please enter a valid Access Code';

            errors.email = !this.validateEmail(email) ? emailMsg : null;
            errors.password = !this.validatePassword(password, isSignUp) ? passwordMsg : null;
            errors.username = !this.validateUsername(username, isSignUp) ? usernameMsg : null;
            errors.accessCode = !this.validateAccessCode(accessCode, isSignUp) ? accessCodeMsg : null;

            if (isSignUp) {
                errors.recaptcha = recaptcha ? null : recaptchaMsg;
            }

            return !errors.email && !errors.username && !errors.password && !errors.recaptcha;
        },
        onInputChange(event) {
            const target = event.target;
            const { name } = target;
            let { value } = target;

            if (name === 'username' && value.length > MAX_USERNAME_LENGTH) {
                value = value.slice(0, MAX_USERNAME_LENGTH);
                this.form.username = value;
                target.value = value;
            }

            const capitalizedName = name.slice(0, 1).toUpperCase() + name.slice(1);

            if (this.errors[name]) {
                if (this[`validate${capitalizedName}`](value, this.signUp)) {
                    this.errors[name] = null;
                }
            }
        },
        async onSubmit() {
            const { signUp, form } = this;
            let serverMsg;

            if (!this.validateForm(form, signUp)) {
                return;
            }

            const url = `/user/${signUp ? 'signUp' : 'login'}`;

            const response = await api.request(url, 'POST', form, false);

            if (response.ok) {
                const msg = `You have ${signUp ? 'signed up' : 'logged in'} successfully!`;
                const authResponse = await response.json();
                this.$store.dispatch('setAuthDetails', authResponse);
                this.$socket.emit('joinDirectmessages', authResponse.userId);

                if (this.redirect) {
                    this.$router.push(this.redirect);
                } else {
                    this.$router.push('/rooms');
                }

                this.$notify({
                    group: 'app',
                    title: 'Success',
                    type: 'success',
                    text: msg,
                });
                return;
            }

            const error = await response.json();

            switch (response.status) {
                case 401:
                    serverMsg = 'The email and/or password are incorrect';
                    break;
                case 404:
                    serverMsg = 'The provided email address does not exist';
                    break;
                case 409:
                    serverMsg = 'The email address or username is already taken';
                    break;
                default:
                    serverMsg = error.message;
            }

            if (response.status === 400 && error.message?.startsWith('ReCaptcha')) {
                window['grecaptcha'].reset();
            }

            this.$notify({
                group: 'app',
                title: 'Error',
                type: 'error',
                text: serverMsg,
            });
        },
        onRecaptchaSuccess(recaptcha) {
            this.form.recaptcha = recaptcha;
            this.errors.recaptcha = null;
        },
		closeTermsAndConditionsModal() {
			this.$store.dispatch('closeModal', 'termsAndConditions');
		},
		openTermsAndConditionsModal() {
			this.$store.dispatch('openModal', { type: 'termsAndConditions' });
		},
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .login-wrapper {
        text-align: center;

        .logo {
            height: auto;
            margin-bottom: 5px;
            max-height: 135px;
            max-width: 165px;
            width: auto;
        }

        .content {
            background: $dark-brown;
            color: $light-gray;
            border-radius: 4px;
            margin: 25px auto;
            max-width: 375px;
            padding: 25px;
            position: relative;
        }

        .form-control-wrapper--username {
            .form-control-with-count {
                position: relative;

                .form-control {
                    padding-right: 48px;
                }

                .char-count {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: .75rem;
                    color: $dark-gray;
                    white-space: nowrap;
                    pointer-events: none;
                }
            }
        }

        .reset-password-btn {
                bottom: -30px;
                position: absolute;
                text-align: center;
                color: $dark-brown;
                left: 50%;
                transform: translateX(-50%);
                font-size: .85em;

                .link {
                    color: $dark-brown;
                }
        }

        .switch-form-toggle {
            margin: 20px auto 0 auto;
        }

        .recaptcha-control-wrapper {
            display: flex;
            flex-direction: column;

            .g-recaptcha {
                align-self: center;
            }
        }
        .recaptcha-hint {
            font-size: .85rem;
            background: white;
            color: $lancer;
            padding: 4px;
            border-radius: 4px;
        }

		.partition {
			border: .5px solid $sage-brown;
			margin-top: 20px;
		}

		.login-terms-and-conditions {
			margin-top: 20px;
			font-size: .75rem;
		}
    }

    @media only screen and (min-width: $screen-sm) {

        .login-wrapper {

            .logo {
                max-height: 175px;
            }
        }
    }

</style>
